import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, BookOpen, Activity, Users, ArrowRight, ScrollText, X, FlaskConical, Heart, Compass, Bookmark, Trash2 } from "lucide-react";
import BioAgeCard from "@/components/BioAgeCard";
import ProtocolCard from "@/components/ProtocolCard";

export default function Dashboard() {
  const { user } = useAuth();
  const [protocols, setProtocols] = useState([]);
  const [savedProtocols, setSavedProtocols] = useState([]);
  const [biomarkers, setBiomarkers] = useState([]);
  const [content, setContent] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [clinic, setClinic] = useState({ name: "", booking_url: "" });
  const [subStatus, setSubStatus] = useState(null);
  const [micro, setMicro] = useState(null);
  const [bioage, setBioage] = useState(null);

  const loadSubStatus = () => {
    api.get("/subscription/status").then((r) => setSubStatus(r.data)).catch(() => {});
  };

  const loadNotifs = () => {
    api.get("/notifications").then((r) => {
      setNotifs(r.data.notifications || []);
      setClinic(r.data.clinic || {});
    }).catch(() => {});
  };

  useEffect(() => {
    api.get("/protocols").then((r) => setProtocols(r.data)).catch(() => {});
    api.get("/my-protocols").then((r) => setSavedProtocols(r.data)).catch(() => {});
    api.get("/biomarkers").then((r) => setBiomarkers(r.data)).catch(() => {});
    api.get("/microbiome/latest").then((r) => setMicro(r.data && r.data.sample ? r.data : null)).catch(() => {});
    api.get("/bioage").then((r) => setBioage(r.data)).catch(() => {});
    api.get("/library").then((r) => setContent(r.data.slice(0, 3))).catch(() => {});
    loadNotifs();
    loadSubStatus();
  }, []);

  const hasPaidPlan = ["vital", "sovereign"].includes(user?.plan);

  const dismissNotif = async (key) => {
    setNotifs((n) => n.filter((x) => x.key !== key));
    try {
      await api.post("/notifications/dismiss", { key });
    } catch {
      loadNotifs();
    }
  };

  const [portalLoading, setPortalLoading] = useState(false);
  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Unable to open the billing portal right now.";
      alert(typeof msg === "string" ? msg : "Unable to open the billing portal right now.");
    } finally {
      setPortalLoading(false);
    }
  };

  const iconForKind = (kind) => {
    const map = {
      welcome: Heart,
      action: Compass,
      reflection: ScrollText,
      lab: FlaskConical,
    };
    return map[kind] || Sparkles;
  };

  const deleteSaved = async (id) => {
    setSavedProtocols((arr) => arr.filter((p) => p.id !== id));
    try { await api.delete(`/my-protocols/${id}`); } catch (e) { console.error("Delete failed", e); }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 pt-16 pb-8 max-w-6xl mx-auto">
        <p className="label-eyebrow mb-3">Today&apos;s tending</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D322B]" data-testid="dashboard-greeting">
          Welcome, {user?.name?.split(" ")[0] || "friend"}.
        </h1>
        <p className="mt-4 text-[#5C6359] max-w-2xl">
          {user?.plan
            ? `Your ${user.plan} path is active. Below: today's protocols, your biomarker trail, and fresh readings from the library.`
            : "You haven't chosen a path yet. The library is open to you — and a full membership unlocks the AI concierge and biomarker tracking."}
        </p>

        {hasPaidPlan && (
          <div className="mt-3">
            <button
              type="button"
              onClick={openBillingPortal}
              disabled={portalLoading}
              data-testid="manage-subscription-btn"
              className="text-xs uppercase tracking-widest text-[#C05C42] hover:text-[#2D322B] inline-flex items-center gap-2 disabled:opacity-60"
            >
              {portalLoading ? "Opening…" : "Manage subscription"} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Subscription state banner — expiring_soon / in_grace / expired */}
        {subStatus?.subscription?.state === "expiring_soon" && (
          <div className="mt-6 bg-[#D49A44]/15 border border-[#D49A44]/40 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="sub-expiring-banner">
            <div className="flex-1">
              <p className="label-eyebrow !text-[#8a6620]">Membership renews soon</p>
              <p className="font-serif text-lg text-[#2D322B] mt-1">
                Your {subStatus.subscription.plan} membership renews in {subStatus.subscription.expires_in_days} {subStatus.subscription.expires_in_days === 1 ? "day" : "days"}.
              </p>
            </div>
            <Link to="/pricing" className="btn-primary !py-2 !px-5" data-testid="renew-cta-expiring">Renew now</Link>
          </div>
        )}
        {subStatus?.subscription?.state === "in_grace" && (
          <div className="mt-6 bg-[#D49A44]/20 border border-[#D49A44]/50 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="sub-grace-banner">
            <div className="flex-1">
              <p className="label-eyebrow !text-[#8a6620]">Grace period</p>
              <p className="font-serif text-lg text-[#2D322B] mt-1">
                Your membership ended — you have {subStatus.subscription.grace_days_left} {subStatus.subscription.grace_days_left === 1 ? "day" : "days"} of grace access. Massey and lab uploads pause after that.
              </p>
            </div>
            <Link to="/pricing" className="btn-primary !py-2 !px-5" data-testid="renew-cta-grace">Renew</Link>
          </div>
        )}
        {subStatus?.subscription?.state === "expired" && (
          <div className="mt-6 bg-[#2D322B] text-[#F9F6F0] rounded-3xl p-8" data-testid="sub-expired-banner">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-5 h-5 text-[#D49A44]" />
              <p className="label-eyebrow !text-[#D49A44]">A note from Massey</p>
            </div>
            {subStatus.reengage_letter ? (
              <p className="font-serif text-lg leading-relaxed text-[#F9F6F0]/95 whitespace-pre-wrap" data-testid="reengage-letter">
                {subStatus.reengage_letter}
              </p>
            ) : (
              <p className="font-serif text-lg leading-relaxed text-[#F9F6F0]/95">
                Your Vital season has paused. The trail remains — whenever you&apos;re ready, Badi opens again.
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/pricing" className="btn-primary !bg-[#D49A44] !text-[#2D322B] hover:!bg-[#bf8a3a]" data-testid="renew-cta-expired">Renew membership</Link>
              <span className="text-xs uppercase tracking-widest text-[#F9F6F0]/60 self-center">Your data stays — even paused.</span>
            </div>
          </div>
        )}

        {/* Your Path — onboarding & re-test reminders */}
        {notifs.length > 0 && (
          <div className="mt-8" data-testid="path-section">
            <div className="flex items-center justify-between mb-3">
              <p className="label-eyebrow">Your path</p>
              <span className="text-xs text-[#5C6359]" data-testid="path-count">{notifs.length} {notifs.length === 1 ? "step" : "steps"}</span>
            </div>
            <div className="space-y-3">
              {notifs.slice(0, 3).map((n) => {
                const Icon = iconForKind(n.kind);
                const isExternal = n.cta_href?.startsWith("http");
                const featured = n.kind === "lab" || n.kind === "welcome";
                return (
                  <div
                    key={n.key}
                    data-testid={`notif-${n.key}`}
                    className={`relative rounded-2xl p-5 border ${featured ? "bg-[#2D322B] text-[#F9F6F0] border-[#2D322B]" : "bg-[#F1ECE4] text-[#2D322B] border-[#D1C7B8]"} flex items-start gap-4`}
                  >
                    <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${featured ? "bg-[#D49A44]/20 text-[#D49A44]" : "bg-[#C05C42]/15 text-[#C05C42]"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg leading-snug">{n.title}</p>
                      <p className={`text-sm mt-1 leading-relaxed ${featured ? "text-[#F9F6F0]/85" : "text-[#5C6359]"}`}>{n.body}</p>
                      {n.cta_label && n.cta_href && (
                        isExternal ? (
                          <a
                            href={n.cta_href}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`notif-cta-${n.key}`}
                            className={`mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest ${featured ? "text-[#D49A44] hover:text-[#F9F6F0]" : "text-[#C05C42] hover:text-[#2D322B]"}`}
                          >
                            {n.cta_label} <ArrowRight className="w-3.5 h-3.5" />
                          </a>
                        ) : (
                          <Link
                            to={n.cta_href}
                            data-testid={`notif-cta-${n.key}`}
                            className={`mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest ${featured ? "text-[#D49A44] hover:text-[#F9F6F0]" : "text-[#C05C42] hover:text-[#2D322B]"}`}
                          >
                            {n.cta_label} <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        )
                      )}
                    </div>
                    <button
                      onClick={() => dismissNotif(n.key)}
                      data-testid={`notif-dismiss-${n.key}`}
                      className={`shrink-0 ${featured ? "text-[#F9F6F0]/50 hover:text-[#F9F6F0]" : "text-[#5C6359] hover:text-[#C05C42]"}`}
                      aria-label="Dismiss"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!hasPaidPlan && (
          <div className="mt-8 bg-[#2D322B] text-[#F9F6F0] rounded-3xl p-8 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="upsell-banner">
            <Sparkles className="w-8 h-8 text-[#D49A44]" />
            <div className="flex-1">
              <p className="label-eyebrow !text-[#D49A44]">Unlock more</p>
              <p className="font-serif text-2xl mt-1">Activate the AI Concierge & biomarker tools.</p>
            </div>
            <Link to="/pricing" className="btn-primary !bg-[#D49A44] !text-[#2D322B] hover:!bg-[#bf8a3a]" data-testid="upgrade-cta">
              See membership
            </Link>
          </div>
        )}

        {/* Biological Age hero */}
        <BioAgeCard bioage={bioage} />
      </section>

      {/* BENTO GRID */}
      <section className="px-6 sm:px-12 lg:px-20 pb-24 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Protocols */}
        <div className="md:col-span-2 bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8" data-testid="protocols-card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <ScrollText className="w-5 h-5 text-[#C05C42]" />
              <p className="label-eyebrow">Your protocols</p>
            </div>
            {!user?.intake_completed && (
              <Link to="/intake" className="text-xs text-[#C05C42] uppercase tracking-widest hover:underline" data-testid="protocol-intake-link">
                Begin intake →
              </Link>
            )}
          </div>
          {protocols.length === 0 ? (
            <div className="text-[#5C6359]">
              <p>No protocols generated yet.</p>
              <Link to="/intake" className="mt-4 inline-block btn-primary" data-testid="start-intake-btn">Start intake</Link>
            </div>
          ) : (
            <div className="space-y-6">
              {protocols.map((p) => (
                <div key={p.id} className="border-b border-[#D1C7B8] pb-6 last:border-0" data-testid={`protocol-${p.id}`}>
                  <p className="text-xs uppercase tracking-widest text-[#C05C42]">{p.tradition}</p>
                  <h3 className="font-serif text-2xl text-[#2D322B] mt-1">{p.title}</h3>
                  <p className="text-sm text-[#5C6359] mt-2">{p.summary}</p>
                  <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-[#2D322B]">
                    {p.practices.map((pr, i) => (
                      <li key={`${p.id}-${i}`} className="flex gap-2"><span className="text-[#D49A44]">◈</span>{pr}</li>
                    ))}
                  </ul>
                  <p className="mt-3 text-xs text-[#5C6359] uppercase tracking-widest">Duration · {p.duration}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Concierge mini-card */}
        <div className="bg-[#2D322B] text-[#F9F6F0] rounded-3xl p-8 flex flex-col" data-testid="concierge-card">
          <Sparkles className="w-6 h-6 text-[#D49A44] mb-6" />
          <p className="label-eyebrow !text-[#D49A44]">AI Concierge</p>
          <h3 className="font-serif text-3xl mt-2 mb-4">Massey</h3>
          <p className="text-sm text-[#F9F6F0]/80 flex-1 leading-relaxed">
            Your grounded guide across precision medicine and ancestral plant wisdom — attuned to your protocols.
          </p>
          <Link to={hasPaidPlan ? "/concierge" : "/pricing"} className="mt-6 inline-flex items-center gap-2 text-[#D49A44] hover:text-[#F9F6F0] text-sm uppercase tracking-widest" data-testid="concierge-cta">
            {hasPaidPlan ? "Open concierge" : "Unlock"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Saved protocols from Massey */}
        {savedProtocols.length > 0 && (
          <div className="md:col-span-3 bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8" data-testid="saved-protocols-card">
            <div className="flex items-center gap-3 mb-6">
              <Bookmark className="w-5 h-5 text-[#C05C42]" />
              <p className="label-eyebrow">Saved from Massey</p>
              <span className="text-xs text-[#5C6359]" data-testid="saved-protocols-count">{savedProtocols.length}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {savedProtocols.map((p) => (
                <ProtocolCard
                  key={p.id}
                  protocol={p}
                  testid={`saved-protocol-${p.id}`}
                  footer={
                    <button
                      onClick={() => deleteSaved(p.id)}
                      data-testid={`delete-saved-${p.id}`}
                      className="text-xs uppercase tracking-widest text-[#8F3A3A] hover:text-[#C05C42] inline-flex items-center gap-1.5"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  }
                />
              ))}
            </div>
          </div>
        )}

        {/* Biomarkers */}
        <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8" data-testid="biomarkers-card">
          <Activity className="w-5 h-5 text-[#C05C42] mb-4" />
          <p className="label-eyebrow">Biomarkers</p>
          <p className="font-serif text-3xl text-[#2D322B] mt-2" data-testid="biomarker-count">{biomarkers.length}</p>
          <p className="text-xs text-[#5C6359] uppercase tracking-widest mt-1">entries recorded</p>
          <Link to={hasPaidPlan ? "/biomarkers" : "/pricing"} className="mt-6 inline-flex items-center gap-2 text-[#C05C42] hover:text-[#2D322B] text-sm uppercase tracking-widest">
            Open ledger <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Microbiome */}
        <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8 flex flex-col" data-testid="microbiome-card">
          <FlaskConical className="w-5 h-5 text-[#C05C42] mb-4" />
          <p className="label-eyebrow">Microbiome</p>
          {micro ? (
            <>
              <p className="font-serif text-3xl text-[#2D322B] mt-2" data-testid="dashboard-diversity">{micro.summary?.diversity_label}</p>
              <p className="text-xs text-[#5C6359] uppercase tracking-widest mt-1">
                diversity · {micro.summary?.species_detected} species
                {micro.summary?.attention_count > 0 && <span className="text-[#8F3A3A]"> · {micro.summary.attention_count} to support</span>}
              </p>
            </>
          ) : (
            <p className="font-serif text-2xl text-[#2D322B] mt-2 leading-snug">Map your inner garden.</p>
          )}
          <Link to={hasPaidPlan ? "/microbiome" : "/pricing"} className="mt-auto pt-6 inline-flex items-center gap-2 text-[#C05C42] hover:text-[#2D322B] text-sm uppercase tracking-widest">
            {micro ? "View terrain" : "Upload FMA report"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Library */}
        <div className="md:col-span-2 bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8" data-testid="library-card">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-5 h-5 text-[#C05C42]" />
            <p className="label-eyebrow">From the library</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {content.map((c) => (
              <Link key={c.id} to={`/library/${c.id}`} className="group block" data-testid={`library-feature-${c.id}`}>
                <div className="rounded-xl overflow-hidden mb-3 h-32 bg-[#D1C7B8]">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <p className="text-xs uppercase tracking-widest text-[#C05C42]">{c.category}</p>
                <p className="font-serif text-base text-[#2D322B] mt-1 leading-snug">{c.title}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Practitioners teaser */}
        <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8 flex flex-col" data-testid="practitioners-card">
          <Users className="w-5 h-5 text-[#C05C42] mb-4" />
          <p className="label-eyebrow">Practitioners</p>
          <p className="font-serif text-2xl text-[#2D322B] mt-2 leading-snug">Meet the keepers of the lineages.</p>
          <Link to="/practitioners" className="mt-auto pt-6 inline-flex items-center gap-2 text-[#C05C42] hover:text-[#2D322B] text-sm uppercase tracking-widest">
            View directory <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Weekly Digest tile */}
        <div className="md:col-span-3 bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8 flex flex-col md:flex-row md:items-center gap-6" data-testid="digest-card">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-5 h-5 text-[#C05C42]" />
              <p className="label-eyebrow">Your weekly read</p>
            </div>
            <p className="font-serif text-2xl text-[#2D322B] leading-snug max-w-xl">
              One quiet page, once a week. Your shifts, a few essays, fresh research, and what we&apos;re reaching for.
            </p>
            <p className="text-sm text-[#5C6359] mt-2">Visit when you want — never pushed.</p>
          </div>
          <Link to="/digest" className="btn-primary inline-flex items-center gap-2" data-testid="digest-cta">
            Open the read <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
